import { Link, useRouterState } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useState, useRef } from "react";
import { Search, Menu, X, ChevronDown, User } from "lucide-react";
import { ShareButton } from "@/components/ShareButton";

const HeaderSearch = lazy(() => import("@/components/HeaderSearch").then((m) => ({ default: m.HeaderSearch })));

const FOOTER_GUIDES = [
  { slug: "starter", title: "Palworld Starter Guide" },
  { slug: "palworld-breeding-guide", title: "Palworld Breeding Guide" },
  { slug: "best-mounts", title: "Best Flying & Ground Mounts" },
  { slug: "tier-list", title: "Meta Tier List" },
  { slug: "palworld-tcg-gameplay-rules", title: "TCG Gameplay Rules" },
  { slug: "palworld-tcg-promo-card-volume-1", title: "Promo Card Volume 1" },
  { slug: "palworld-card-grading-services", title: "Card Grading: PSA & Beckett" },
  { slug: "palworld-eternal-ascent-trial-decks", title: "Eternal Ascent Trial Decks" },
  { slug: "palworld-cake-guide", title: "Cake Guide & FAQ" },
  { slug: "palworld-news-2026", title: "Palworld News 2026" },
];



/** Official TikTok glyph — lucide has no brand mark, so we draw it inline. */
function TikTokIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M16.6 5.82A4.28 4.28 0 0 0 15.54 3h-3.1v12.3a2.6 2.6 0 1 1-1.85-2.49V9.66a5.7 5.7 0 1 0 4.95 5.64V8.9a7.2 7.2 0 0 0 4.06 1.25V7.05a4.28 4.28 0 0 1-3-1.23z" />
    </svg>
  );
}

/** Instagram glyph (rounded square + lens + flash dot). */
function InstagramIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" focusable="false">
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.2" cy="6.8" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** X (formerly Twitter) glyph — lucide has no brand mark, so we draw it inline. */
function XIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M17.53 3h3.02l-6.6 7.54L21.5 21h-5.9l-4.2-5.53L6.6 21H3.57l6.87-7.85L2.9 3h6.05l3.9 5.16L17.53 3zm-1.06 16.2h1.67L7.6 4.71H5.81L16.47 19.2z" />
    </svg>
  );
}

/** YouTube glyph — lucide has no brand mark, so we draw it inline. */
function YouTubeIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M23.5 6.19c-.28-1.03-1.1-1.85-2.13-2.13C19.51 3.5 12 3.5 12 3.5s-7.51 0-9.37.56c-1.03.28-1.85 1.1-2.13 2.13C0 8.05 0 12 0 12s0 3.95.5 5.81c.28 1.03 1.1 1.85 2.13 2.13C4.49 20.5 12 20.5 12 20.5s7.51 0 9.37-.56c1.03-.28 1.85-1.1 2.13-2.13.5-1.86.5-5.81.5-5.81s0-3.95-.5-5.81zM9.55 15.5V8.5l6.23 3.5-6.23 3.5z" />
    </svg>
  );
}



/**
 * Navigation is grouped: every dropdown is split into short, labelled sections
 * instead of one long scrolling list, so no menu shows more than ~5 links in a
 * column. Games now also owns the Packs and Palsino pages.
 */
const PALS_MENU: MenuGroup[] = [
  {
    heading: "Database",
    items: [
      { to: "/pals", label: "All Pals" },
      { to: "/palworld-wiki", label: "Palworld Wiki" },
      { to: "/palworld-tcg", label: "Palworld TCG" },
      { to: "/tier-list", label: "Tier List" },
      { to: "/capture-rates", label: "Capture Rates" },
      { to: "/breeding", label: "Breeding" },
      { to: "/items", label: "Items & Weapons" },
      { to: "/passives", label: "Passive Skills" },
      { to: "/favorites", label: "Your Favorites" },
    ],
  },

  {
    heading: "Tools & calculators",
    items: [
      { to: "/team-builder", label: "Team Builder" },
      { to: "/technology-tree", label: "Technology Tree" },
      { to: "/mounts", label: "Mounts & Speeds" },
      { to: "/elements", label: "Elements Chart" },
      { to: "/damage", label: "Damage Calculator" },
    ],
  },
];

const GUIDES_MENU: MenuGroup[] = [
  {
    heading: "Start here",
    items: [
      { to: "/guides", label: "All Guides & Tier Lists" },
      { to: "/guides/$slug", label: "Palworld Starter Guide", params: { slug: "starter" } },
      { to: "/guides/$slug", label: "Palworld News 2026", params: { slug: "palworld-news-2026" } },
      { to: "/guides/$slug", label: "1.0 Patch & Updates", params: { slug: "palworld-1-0-patch-updates-guide" } },
    ],
  },
  {
    heading: "Game guides",
    items: [
      { to: "/eggs", label: "Palworld Eggs" },
      { to: "/guides/$slug", label: "All Eggs Guide & FAQ", params: { slug: "palworld-all-eggs-guide" } },
      { to: "/guides/$slug", label: "Cake Guide & FAQ", params: { slug: "palworld-cake-guide" } },
      { to: "/guides/$slug", label: "Palworld Mobile", params: { slug: "palworld-mobile-how-to-play" } },
    ],
  },
  {
    heading: "Spin-offs & cards",
    items: [
      { to: "/guides/$slug", label: "PalFarm Mobile", params: { slug: "palfarm-mobile-guide" } },
      { to: "/guides/$slug", label: "Just More Than Pals", params: { slug: "more-than-pals-guide" } },
      { to: "/guides/$slug", label: "Card Grading: PSA & Beckett", params: { slug: "palworld-card-grading-services" } },
      { to: "/guides/$slug", label: "Eternal Ascent Trial Decks", params: { slug: "palworld-eternal-ascent-trial-decks" } },
    ],
  },
];

const BOSS_MENU: MenuGroup[] = [
  {
    heading: "Bosses",
    items: [
      { to: "/bosses", label: "All Bosses" },
      { to: "/boss-route", label: "Boss Route Planner" },
      { to: "/damage", label: "Damage Calculator" },
      { to: "/map", label: "Interactive Map" },
      { to: "/guides/$slug", label: "All Bosses Guide & FAQ", params: { slug: "palworld-all-bosses-guide" } },
    ],
  },
];

const CARDS_MENU: MenuGroup[] = [
  {
    heading: "Card database",
    items: [
      { to: "/cards", label: "All TCG Cards" },
      { to: "/card-checker", label: "Card Checker" },
      { to: "/deck-builder", label: "Deck Builder" },
      { to: "/guides/$slug", label: "Card Rarity Guide", params: { slug: "palworld-card-rarity" } },
    ],
  },
  {
    heading: "Collect & trade",
    items: [
      { to: "/collection", label: "Collection Tracker" },
      { to: "/binder", label: "Collector Binder" },
      { to: "/trades", label: "Card Trading" },
      { to: "/want-list", label: "Want List Board" },
      { to: "/players", label: "Player Profiles" },
    ],
  },
  {
    heading: "Read",
    items: [
      { to: "/blog", label: "Palworld TCG Blog" },
      { to: "/tcg-tools", label: "TCG Tools" },
      { to: "/palworld-tcg", label: "TCG Topic Hub" },
    ],
  },
];

const MARKET_MENU: MenuGroup[] = [
  {
    heading: "Prices & value",
    items: [
      { to: "/tcg-price-index", label: "TCG Price Index" },
      { to: "/prices", label: "TCG Price Chart" },
      { to: "/palworld-card-value", label: "Palworld Card Value" },
      { to: "/most-expensive-palworld-cards", label: "Most Expensive Palworld Cards" },
      { to: "/graded-card-prices", label: "Graded Card Prices (PSA/BGS)" },
      { to: "/card-value-calculator", label: "Card Value Calculator" },
      { to: "/pack-calculator", label: "Pack Calculator" },
      { to: "/palworld-alternate-art-price-guide", label: "Alternate Art Price Guide" },
      { to: "/tcg-market-report", label: "Weekly Market Report" },
    ],
  },
  {
    heading: "Buy cards",
    items: [
      { to: "/card-marketplace", label: "Palworld TCG Card Marketplace" },
      { to: "/marketplace", label: "Sealed & Merch Marketplace" },
      { to: "/tcg-bulk-order", label: "Palworld TCG Bulk Order" },
      { to: "/cards", label: "Browse All Cards" },
    ],
  },
];

const TCG_RULES_MENU: MenuGroup[] = [
  {
    heading: "Rules",
    items: [
      { to: "/guides/$slug", label: "TCG Gameplay Rules & FAQ", params: { slug: "palworld-tcg-gameplay-rules" } },
      { to: "/guides/$slug", label: "Card Game Rules & Rewards", params: { slug: "pal-stars-card-game-rules-and-rewards" } },
      { to: "/guides/$slug", label: "Pal Stars Card Game Guide", params: { slug: "palstars-palworld-card-game" } },
      { to: "/tcg-tools", label: "TCG Tools" },
    ],
  },
  {
    heading: "Promos & packs",
    items: [
      { to: "/guides/$slug", label: "Promo Card Volume 1", params: { slug: "palworld-tcg-promo-card-volume-1" } },
      { to: "/guides/$slug", label: "Promo Soul Cards", params: { slug: "palworld-tcg-promo-soul-cards" } },
      { to: "/guides/$slug", label: "Pack Opening Guide & FAQ", params: { slug: "palworld-card-pack-opening" } },
      { to: "/binder", label: "Collector Binder" },
    ],
  },
];

const GAMES_MENU: MenuGroup[] = [
  {
    heading: "Arcade",
    items: [
      { to: "/palworld-games", label: "All Free Palworld Games" },
      { to: "/weekly-tournament", label: "Weekly Tournament" },
      { to: "/daily-puzzle", label: "Daily Pal Puzzle" },
      { to: "/leaderboard", label: "Weekly Leaderboard" },
    ],
  },
  {
    heading: "Action & quiz",
    items: [
      { to: "/pal-catch", label: "Pal Sphere Toss" },
      { to: "/card-match", label: "TCG Memory Match" },
      { to: "/whos-that-pal", label: "Who's That Pal?" },
      { to: "/type-duel", label: "Type Chart Duel" },
      { to: "/cake-bake", label: "Cake Baking Simulator" },
      { to: "/build-sim", label: "Base Building Simulator" },
    ],
  },
  {
    heading: "Strategy & market",
    items: [
      { to: "/fusion-lab", label: "Pal Fusion Lab" },
      { to: "/pal-draft", label: "Pal Draft Roulette" },
      { to: "/pal-bracket", label: "Best Pal Bracket" },
      { to: "/price-guess", label: "Card Price Quiz" },
      { to: "/card-market", label: "Card Market Flip" },
    ],
  },
  {
    heading: "Packs & Palsino",
    items: [
      { to: "/palsino", label: "Palsino Casino" },
      { to: "/booster-pack", label: "Booster Packs" },
      { to: "/pack-opening", label: "Pack Opening Game" },
      { to: "/rewards", label: "Free Packs & Rewards" },
    ],
  },
];



type MenuItem = { to: string; label: string; params?: { slug: string } };
type MenuGroup = { heading: string; items: MenuItem[] };

/** Flattens grouped menu data for active-state and mobile rendering. */
function flatten(groups: readonly MenuGroup[]): MenuItem[] {
  return groups.flatMap((g) => g.items);
}

/**
 * Desktop nav dropdown: hover-opens on pointer devices, click-toggles on touch,
 * closes on outside pointer-down or Escape, and fades/slides in and out.
 * Multi-group menus render as labelled columns so the panel never becomes a
 * long scrolling list.
 */
function NavDropdown({ label, groups, active = false }: { label: string; groups: readonly MenuGroup[]; active?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: Event) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const canHover = () =>
    typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  return (
    <div
      ref={ref}
      className="relative shrink-0"
      onMouseEnter={() => { if (timer.current) clearTimeout(timer.current); if (canHover()) setOpen(true); }}
      onMouseLeave={() => { if (canHover()) { timer.current = setTimeout(() => setOpen(false), 140); } }}
      onBlur={(e) => {
        // Keyboard users tabbing past the last item should close the menu.
        if (!ref.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        type="button"
        onClick={() => {
          // On hover-capable pointers the menu is already open from mouseenter,
          // so a click must keep it open (toggling here closed it immediately).
          if (canHover()) setOpen(true);
          else setOpen((v) => !v);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            setOpen(true);
            requestAnimationFrame(() => {
              const links = ref.current?.querySelectorAll<HTMLAnchorElement>('[role="menuitem"]');
              if (!links?.length) return;
              (e.key === "ArrowDown" ? links[0] : links[links.length - 1])?.focus();
            });
          }
        }}
        className={`inline-flex items-center gap-0.5 whitespace-nowrap rounded-md px-1 py-2 2xl:px-1.5 transition-colors hover:bg-card hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
          active
            ? "bg-card text-foreground font-semibold shadow-[inset_0_-2px_0_0_var(--color-primary)]"
            : open
              ? "bg-card text-foreground"
              : "text-muted-foreground"
        }`}
        aria-haspopup="menu"
        aria-expanded={open}
        {...(active ? { "aria-current": "page" as const } : {})}
      >
        <span>{label}</span> <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      <div
        role="menu"
        aria-label={label}
        aria-hidden={!open}
        className={`absolute top-full z-50 pt-1 origin-top transition-all duration-200 ease-out ${
          groups.length > 2 ? "right-0" : "left-0"
        } ${open ? "opacity-100 translate-y-0 scale-100" : "pointer-events-none opacity-0 -translate-y-1 scale-95"}`}
      >
        <div
          className={`grid gap-x-3 gap-y-2 rounded-xl border border-border bg-popover p-2 shadow-2xl ${
            groups.length >= 4 ? "grid-cols-4" : groups.length === 3 ? "grid-cols-3" : groups.length === 2 ? "grid-cols-2" : "grid-cols-1"
          }`}
          style={{ minWidth: groups.length > 1 ? 184 * Math.min(groups.length, 4) : 196 }}
        >
          {groups.map((group) => (
            <div key={group.heading} className="min-w-0">
              {groups.length > 1 ? (
                <p className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-widest text-primary">{group.heading}</p>
              ) : null}
              {group.items.map((s) => (
            <Link
              key={s.to + s.label}
              to={s.to as never}
              params={s.params as never}
              role="menuitem"
              tabIndex={open ? 0 : -1}
              onClick={() => setOpen(false)}
              onKeyDown={(e) => {
                // Roving arrow/Home/End navigation inside the open menu; Escape
                // closes and returns focus to the trigger button.
                const links = Array.from(ref.current?.querySelectorAll<HTMLAnchorElement>('[role="menuitem"]') ?? []);
                const i = links.indexOf(e.currentTarget as HTMLAnchorElement);
                if (e.key === "ArrowDown") { e.preventDefault(); links[(i + 1) % links.length]?.focus(); }
                else if (e.key === "ArrowUp") { e.preventDefault(); links[(i - 1 + links.length) % links.length]?.focus(); }
                else if (e.key === "Home") { e.preventDefault(); links[0]?.focus(); }
                else if (e.key === "End") { e.preventDefault(); links[links.length - 1]?.focus(); }
                else if (e.key === "Escape" || e.key === "Tab") {
                  if (e.key === "Escape") {
                    e.preventDefault();
                    ref.current?.querySelector<HTMLButtonElement>("button")?.focus();
                  }
                  setOpen(false);
                }
              }}
              activeOptions={{ exact: true }}
              activeProps={{ className: "bg-card text-foreground font-semibold", "aria-current": "page" }}
              className="block rounded-md px-3 py-2 text-sm hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >{s.label}</Link>
              ))}
            </div>
          ))}
        </div>

      </div>
    </div>

  );
}

/**
 * Mobile accordion section — tap the header to expand its grouped links. Groups
 * keep each expanded panel short and scannable on a phone.
 */
function MobileSection({ label, groups, onNavigate }: { label: string; groups: readonly MenuGroup[]; onNavigate: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between rounded-lg px-3 py-3 text-left font-semibold hover:bg-card"
      >
        {label}
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <div
        className={`grid overflow-hidden transition-all duration-300 ease-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
      >
        <div className="min-h-0">
          <div className="flex flex-col gap-2 px-2 pb-2">
            {groups.map((group) => (
              <div key={group.heading}>
                {groups.length > 1 ? (
                  <p className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-widest text-primary">{group.heading}</p>
                ) : null}
                <div className="flex flex-col gap-0.5">
                  {group.items.map((s) => (
                    <Link
                      key={s.to + s.label}
                      to={s.to as never}
                      params={s.params as never}
                      onClick={onNavigate}
                      activeOptions={{ exact: true }}
                      activeProps={{ className: "bg-card font-semibold text-foreground", "aria-current": "page" }}
                      className="rounded-md px-3 py-2.5 text-muted-foreground hover:bg-card hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >{s.label}</Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


export function SiteHeader() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const hrefOf = (s: MenuItem) => (s.params ? s.to.replace("$slug", s.params.slug) : s.to);
  const isGroupActive = (items: readonly MenuItem[]) => items.some((s) => pathname === hrefOf(s));


  const [menuOpen, setMenuOpen] = useState(false);

  // Any navigation closes the mobile panel so it never covers the new page.
  useEffect(() => { setMenuOpen(false); }, [pathname]);


  useEffect(() => {
    const isTypingTarget = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      if (!el) return false;
      return el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);
    };
    const focusSearch = () => {
      (document.getElementById("global-search") as HTMLInputElement | null)?.focus();
    };
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        focusSearch();
        return;
      }
      // "/" is the conventional search shortcut; ignore it while typing elsewhere.
      if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey && !isTypingTarget(e.target)) {
        e.preventDefault();
        focusSearch();
        return;
      }
      if (e.key === "Escape") setMenuOpen(false);
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);





  return (
    <header className="sticky top-0 z-40 glass border-b">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-2 px-3 sm:px-4 xl:px-6">
        <Link to="/" className="flex items-center gap-2 shrink-0" aria-label="Pal Stars home">
          <img src="/eidrolon-avatar-72.webp" alt="" aria-hidden="true" width={36} height={36} decoding="async" fetchPriority="high" className="h-9 w-9 rounded-lg object-cover" />
          <span className="font-display text-base sm:text-lg font-bold tracking-tight text-white">
            Pal <span className="text-white">Stars</span>
          </span>
        </Link>


        <nav aria-label="Main" className="hidden min-w-0 flex-1 overflow-visible xl:flex items-center justify-center gap-0 text-xs 2xl:text-sm">
          <NavDropdown label="Pals" groups={PALS_MENU} active={isGroupActive(flatten(PALS_MENU))} />
          <NavDropdown label="Guides" groups={GUIDES_MENU} active={isGroupActive(flatten(GUIDES_MENU))} />
          <NavDropdown label="Boss" groups={BOSS_MENU} active={isGroupActive(flatten(BOSS_MENU))} />
          <NavDropdown label="Cards" groups={CARDS_MENU} active={isGroupActive(flatten(CARDS_MENU))} />
          <NavDropdown label="TCG Rules" groups={TCG_RULES_MENU} active={isGroupActive(flatten(TCG_RULES_MENU))} />
          <NavDropdown label="Marketplace" groups={MARKET_MENU} active={isGroupActive(flatten(MARKET_MENU))} />
          <NavDropdown label="Games" groups={GAMES_MENU} active={isGroupActive(flatten(GAMES_MENU))} />
          <Link
            to="/blog"
            className="rounded-md px-1 py-1.5 font-semibold hover:bg-card whitespace-nowrap 2xl:px-1.5"
            activeProps={{ className: "rounded-md px-1 py-1.5 font-semibold bg-card text-primary whitespace-nowrap 2xl:px-1.5" }}
          >
            Blog
          </Link>

        </nav>



        <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">


          <SearchSlot />

          <ShareButton
            title="Pal Stars — Palworld Wiki & TCG Prices"
            text="Check out this Palworld data on Pal Stars!"
            className="global-share-btn inline-flex h-8 items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-2.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/20"
          />


          <Link to="/account" className="hidden sm:inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border px-2.5 text-xs font-semibold leading-none hover:bg-card whitespace-nowrap" aria-label="Your account">
            <User className="h-3.5 w-3.5 shrink-0" /> Account
          </Link>

          <a href="https://tiktok.com/@palworldtcg" target="_blank" rel="noopener noreferrer" aria-label="TikTok — @palworldtcg" title="Pal Stars on TikTok" className="hidden 2xl:inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-card">
            <TikTokIcon />
          </a>
          <a href="https://instagram.com/palworld.tcg" target="_blank" rel="noopener noreferrer" aria-label="Instagram — @palworld.tcg" title="Pal Stars on Instagram" className="hidden 2xl:inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-card">
            <InstagramIcon />
          </a>
          <a href="https://x.com/palsphere" target="_blank" rel="noopener noreferrer" aria-label="X — @palsphere" title="Pal Stars on X" className="hidden 2xl:inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-card">
            <XIcon />
          </a>
          <a href="https://www.youtube.com/@Palspheres" target="_blank" rel="noopener noreferrer" aria-label="YouTube — @Palspheres" title="Pal Stars on YouTube" className="hidden 2xl:inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-card">
            <YouTubeIcon />
          </a>







          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            className="xl:hidden inline-flex h-11 w-11 items-center justify-center rounded-md hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav
          id="mobile-nav"
          aria-label="Mobile"
          className="xl:hidden max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain border-t px-4 py-3 flex flex-col gap-1.5 text-sm animate-fade-in"
        >
          <Link
            to="/"
            onClick={(e) => {
              e.preventDefault();
              setMenuOpen(false);
              window.setTimeout(() => (document.getElementById("global-search") as HTMLInputElement | null)?.focus(), 60);
            }}
            className="mb-2 flex min-h-11 items-center gap-2 rounded-lg border px-3 text-sm text-muted-foreground hover:bg-card"
            aria-label="Search Pals, cards, guides and tools"
          >
            <Search className="h-4 w-4 shrink-0" />
            Search Pals, cards, guides &amp; games…
          </Link>

          <div className="mb-2 flex items-center justify-end gap-2">
            <Link to="/account" onClick={() => setMenuOpen(false)} className="sm:hidden inline-flex h-10 items-center gap-1.5 rounded-md border px-3 text-xs font-semibold hover:bg-card" aria-label="Your account">
              <User className="h-3.5 w-3.5" /> Account
            </Link>
          </div>
          <MobileSection label="Pals & Tools" groups={PALS_MENU} onNavigate={() => setMenuOpen(false)} />
          <MobileSection label="Guides" groups={GUIDES_MENU} onNavigate={() => setMenuOpen(false)} />
          <MobileSection label="Boss" groups={BOSS_MENU} onNavigate={() => setMenuOpen(false)} />
          <MobileSection label="Cards" groups={CARDS_MENU} onNavigate={() => setMenuOpen(false)} />
          <MobileSection label="TCG Rules" groups={TCG_RULES_MENU} onNavigate={() => setMenuOpen(false)} />
          <MobileSection label="Marketplace & Prices" groups={MARKET_MENU} onNavigate={() => setMenuOpen(false)} />
          <MobileSection label="Games, Packs & Palsino" groups={GAMES_MENU} onNavigate={() => setMenuOpen(false)} />


          <div className="mt-3 flex items-center gap-2 border-t pt-3">
            <span className="text-xs text-muted-foreground">Follow Pal Stars</span>
            <a href="https://tiktok.com/@palworldtcg" target="_blank" rel="noopener noreferrer" aria-label="TikTok — @palworldtcg"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md hover:bg-card"><TikTokIcon /></a>
            <a href="https://instagram.com/palworld.tcg" target="_blank" rel="noopener noreferrer" aria-label="Instagram — @palworld.tcg"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md hover:bg-card"><InstagramIcon /></a>
            <a href="https://x.com/palsphere" target="_blank" rel="noopener noreferrer" aria-label="X — @palsphere"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md hover:bg-card"><XIcon /></a>
          </div>
        </nav>
      )}

    </header>
  );
}



function SearchSlot() {
  const [active, setActive] = useState(false);

  if (active) {
    return (
      <Suspense fallback={<SearchShell onActivate={() => setActive(true)} />}>
        <HeaderSearch />
      </Suspense>
    );
  }

  return <SearchShell onActivate={() => setActive(true)} />;
}

function SearchShell({ onActivate }: { onActivate: () => void }) {
  return (
    <div className="relative">
      <div className="flex items-center gap-1.5 rounded-lg glass px-2 py-1.5">
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
        <input
          id="global-search"
          readOnly
          onFocus={onActivate}
          onPointerDown={onActivate}
          aria-label="Search Pals, cards, guides and tools"
          placeholder="Search…"
          className="bg-transparent outline-none text-sm w-14 sm:w-16 2xl:w-28"
        />
      </div>
    </div>
  );
}


export function SiteFooter() {
  return (

    <footer className="border-t mt-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12 text-sm text-muted-foreground">
        <div className="grid gap-10 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          <div className="sm:col-span-2 md:col-span-3 lg:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <img src="/eidrolon-avatar-72.webp" alt="" aria-hidden="true" width={36} height={36} loading="lazy" decoding="async" className="h-9 w-9 rounded-lg object-cover" />
              <span className="font-display text-lg font-bold text-foreground">Pal Stars</span>
            </div>
            <p className="max-w-md mb-4">
              Pal Stars: Palworld TCG Price Index, Cards, and Palworld Wiki.
            </p>
          </div>

          <div>
            <div className="font-semibold text-foreground mb-3">External Links</div>
            <div className="flex flex-col gap-1.5">
              <Link to="/press" className="hover:text-foreground">Press &amp; Media</Link>
              <a href="https://palworld-official-cardgame.com/en/" target="_blank" rel="noopener noreferrer nofollow" className="hover:text-foreground">
                Official Palworld TCG ↗
              </a>
              <a href="https://www.pocketpair.jp/palworld" target="_blank" rel="noopener noreferrer nofollow" className="hover:text-foreground">
                Official Palworld ↗
              </a>
            </div>
          </div>

          <div>
            <div className="font-semibold text-foreground mb-3">Contact Us</div>
            <div className="flex flex-col gap-1.5">
              <Link to="/contact" className="hover:text-foreground">Contact Us</Link>
              <Link to="/about" className="hover:text-foreground">About Pal Stars</Link>
              <Link to="/guides" className="hover:text-foreground">All Guides</Link>
            </div>
          </div>

          <div>
            <div className="font-semibold text-foreground mb-3">Legal &amp; Security</div>
            <div className="flex flex-col gap-1.5">
              <Link to="/privacy" className="hover:text-foreground">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-foreground">Terms of Service</Link>
              <Link to="/security-disclaimer" className="hover:text-foreground">Security Disclaimer</Link>
            </div>
          </div>


        </div>

        <div className="mt-10 pt-6 border-t">
          <nav aria-label="Palworld guides" className="[&_ul]:grid [&_ul]:gap-1.5 [&_ul]:sm:grid-cols-2 [&_ul]:lg:grid-cols-4">
            <div className="font-semibold text-foreground mb-3">Palworld guides</div>
            <ul>
              {FOOTER_GUIDES.map((g) => (
                <li key={g.slug}>
                  <Link to="/guides/$slug" params={{ slug: g.slug }} className="hover:text-foreground">
                    {g.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/guides" className="text-primary hover:underline">
                  All Palworld guides →
                </Link>
              </li>
            </ul>
          </nav>
        </div>


        <div className="mt-10 pt-6 border-t space-y-3 text-xs">
          <p className="max-w-4xl">
            <span className="font-semibold text-foreground">Legal Disclaimer:</span> palstars.com is an independent
            community database, price index tracking tool, and reference resource. This platform is not affiliated with, endorsed by, sponsored by,
            authorized by, or connected in any way with Pocketpair, Inc., Bushiroad, or any of
            their licensed distribution subsidiaries. All associated Palworld creature designations, asset models,
            card imagery components, and trademarked indicators belong exclusively to their respective corporate
            intellectual property holders. Card images and game text are displayed strictly for educational, review,
            and identification purposes under Fair Use guidelines. Market pricing data points represent aggregated
            trading estimates derived from tracking parameters and should not be used as explicit financial
            evaluation guidance.
          </p>

          <p>
            &copy; 2026 Pal Stars. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
